// Business portal home (FR-40): the companies the person is staff of, and a
// form to register a new one. A new company is pending until a platform
// administrator approves it; its registrant becomes its company_admin.
import { Button, Group, Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { FormError, Notice, WidePage } from "../../shared/ui/Page";
import { CompanyStatusBadge, MINE_KEY, RoleBadge, useMyCompanies, type Company } from "./common";
import { CompanyForm, type CompanyDetails } from "./CompanyForm";

export function BusinessPage() {
  return (
    <SignedIn>
      <Business />
    </SignedIn>
  );
}

function Business() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mine = useMyCompanies();
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState<Company | null>(null);
  const items = mine.data?.items ?? [];

  const register = async (details: CompanyDetails): Promise<string | null> => {
    // A registration always starts pending; only a platform admin activates it.
    const { data, error } = await api.POST("/companies", { body: { ...details, status: "pending" } });
    if (!data) return errorMessage(error, t("error.generic"));
    setRegistered(data);
    setRegistering(false);
    await queryClient.invalidateQueries({ queryKey: MINE_KEY });
    return null;
  };

  return (
    <WidePage title={t("business.title")} intro={t("business.intro")}>
      {registered && (
        <Notice>
          <Text size="sm" fw={600}>
            {t("business.registered", { name: registered.name })}
          </Text>
          {registered.status === "pending" && <Text size="sm">{t("business.registered_pending")}</Text>}
        </Notice>
      )}
      {registering ? (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Title order={2} size="h4">
              {t("business.register")}
            </Title>
            <Text size="sm" c="dimmed">
              {t("business.register_intro")}
            </Text>
            <CompanyForm
              submitLabel={t("business.register_submit")}
              onSubmit={register}
              onCancel={() => setRegistering(false)}
            />
          </Stack>
        </Paper>
      ) : (
        <Button
          w="fit-content"
          onClick={() => {
            setRegistered(null);
            setRegistering(true);
          }}
        >
          {t("business.register")}
        </Button>
      )}
      <Title order={2} size="h3">
        {t("business.mine")}
      </Title>
      {mine.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : mine.isError ? (
        <FormError message={errorMessage(mine.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="business-empty">
          {t("business.empty")}
        </Text>
      ) : (
        <Stack gap="sm" component="ul" p={0} m={0} style={{ listStyle: "none" }} aria-label={t("business.mine")}>
          {items.map(({ company: c, role }) => (
            <li key={c.id}>
              <Paper withBorder p="md" radius="md" data-testid="my-company">
                <Group justify="space-between" align="flex-start" gap="sm">
                  <Stack gap={4}>
                    <Title order={3} size="h4">
                      {c.name}
                    </Title>
                    <Text size="xs" c="dimmed">
                      {c.public_ref} · {t(`segment.${c.segment}`, { defaultValue: c.segment })}
                    </Text>
                    <Group gap={6}>
                      <CompanyStatusBadge status={c.status} />
                      <RoleBadge role={role} />
                    </Group>
                  </Stack>
                  <Button
                    component={Link}
                    to={`/business/${c.id}`}
                    size="xs"
                    variant="light"
                    aria-label={t("business.open_for", { name: c.name })}
                  >
                    {t("business.open")}
                  </Button>
                </Group>
              </Paper>
            </li>
          ))}
        </Stack>
      )}
    </WidePage>
  );
}
